import LogFactory, { C9Logger, LogLevels } from 'c9-js-log-client';
import { createContext, ReactElement, useCallback, useContext, useEffect, useMemo } from 'react';

/* ================================================ Export Interfaces ================================================ */
export type TLogger = C9Logger;

export type TLogLevels = keyof typeof LogLevels;

/* ================================================ Local Interfaces ================================================ */
interface ILoggerContextProviderWrapper {
    values: [logEndpoint: string, appName: string, logLevel?: TLogLevels];
    children: ReactElement;
}

type ILoggerContext = { [key: string]: (errorMessage: string) => void };

/* ================================================ Local Functions ================================================ */
// ================================================ Specific Page
const useVehiclePage = (logger: TLogger) => {
    const logCallDMRGetVehicleError = useCallback(
        (errorMessage: string) => {
            logger.error(`DMR failed: ${errorMessage}`);
        },
        [logger]
    );

    const logCallMMGetVehicleDetailsError = useCallback(
        (errorMessage: string) => {
            logger.error(`MM failed: ${errorMessage}`);
        },
        [logger]
    );

    return { logCallDMRGetVehicleError, logCallMMGetVehicleDetailsError };
};

const useExperiencePage = (logger: TLogger) => {
    const logGetCarVerificationError = useCallback(
        (errorMessage: string) => {
            logger.error(`Car verification failed: ${errorMessage}`);
        },
        [logger]
    );

    return { logGetCarVerificationError };
};

// ================================================ Shared
const useMileageHelperModal = (logger: TLogger) => {
    const logUpdateMileageError = useCallback(
        (errorMessage: string) => {
            logger.error(`Failed to update mileage, error: ${errorMessage}`);
        },
        [logger]
    );

    return { logUpdateMileageError };
};

const usePackagePage = (logger: TLogger) => {
    const logUpdatePricingBasedOnDisabilitySumError = useCallback(
        (errorMessage: string) => {
            logger.error(`Could not update the pricing based on disability sum, error: ${errorMessage}`);
        },
        [logger]
    );

    const logUpdatePricingBasedOnMileageError = useCallback(
        (errorMessage: string) => {
            logger.error(`Could not update the pricing based on mileage, error: ${errorMessage}`);
        },
        [logger]
    );

    const logUpdatePricingBasedOnDeductibleError = useCallback(
        (errorMessage: string) => {
            logger.error(`Could not update the pricing based on deductible, error: ${errorMessage}`);
        },
        [logger]
    );

    const logHandleCoverageGroupSelectionError = useCallback(
        (errorMessage: string) => {
            logger.error(`Could not update the package when changing coverage group selection, error: ${errorMessage}`);
        },
        [logger]
    );

    const logUpdatePackageError = useCallback(
        (errorMessage: string) => {
            logger.error(`Could not update the package when selecting additional coverage, error: ${errorMessage}`);
        },
        [logger]
    );

    return {
        logUpdatePricingBasedOnDisabilitySumError,
        logUpdatePricingBasedOnMileageError,
        logUpdatePricingBasedOnDeductibleError,
        logHandleCoverageGroupSelectionError,
        logUpdatePackageError
    };
};

const usePackageLoader = (logger: TLogger) => {
    const logHandlePackagesRequestError = useCallback(
        (errorMessage: string) => {
            logger.error(errorMessage);
        },
        [logger]
    );

    return { logHandlePackagesRequestError };
};

const usePaymentPage = (logger: TLogger) => {
    const logValidateBankAccountError = useCallback(
        (errorMessage: string) => {
            logger.error(errorMessage);
        },
        [logger]
    );

    return { logValidateBankAccountError };
};

const useConfirmPage = (logger: TLogger) => {
    const logFetchDMRDataError = useCallback(
        (errorMessage: string) => {
            logger.error(`Failed to fetch DMR data when user logged in, error: ${errorMessage}`);
        },
        [logger]
    );

    const logHandleContactProceedError = useCallback(
        (errorMessage: string) => {
            logger.error(`Failed when handle contact proceed when handle continue, error: ${errorMessage}`);
        },
        [logger]
    );

    return { logFetchDMRDataError, logHandleContactProceedError };
};

const useFinalizePurchasePage = (logger: TLogger) => {
    const logFinalizePurchaseError = useCallback(
        (errorMessage: string) => {
            logger.error(`Error while finalizing purchase: ${errorMessage}`);
        },
        [logger]
    );

    const logFinalizePurchaseEmailError = useCallback(
        (errorMessage: string) => {
            logger.error(`Error while sending email after purchase finalized: ${errorMessage}`);
        },
        [logger]
    );

    return { logFinalizePurchaseError, logFinalizePurchaseEmailError };
};

// ================================================ General
const useLogger = (logger: TLogger) => {
    const logError = useCallback(
        (errorMessage: string) => {
            logger.error(errorMessage);
        },
        [logger]
    );

    const logWarn = useCallback(
        (message: string) => {
            logger.warn(message);
        },
        [logger]
    );

    const logDebug = useCallback(
        (message: string) => {
            logger.debug(message);
        },
        [logger]
    );

    return { logError, logWarn, logDebug };
};
const useFetchContent = (logger: TLogger) => {
    const logFetchContentError = useCallback(
        (errorMessage: string) => {
            logger.error(`Error fetching content objects: ${errorMessage}`);
        },
        [logger]
    );

    return { logFetchContentError };
};

const useUser = (logger: TLogger) => {
    const logGetCustomerMarkingError = useCallback(
        (errorMessage: string) => {
            logger.error(`Failed to get user's customer marking when initiating user data, error: ${errorMessage}`);
        },
        [logger]
    );

    const logGetStakeholderError = useCallback(
        (errorMessage: string) => {
            logger.error(`Failed to get user's stakeholder data when initiating user data, error: ${errorMessage}`);
        },
        [logger]
    );

    const logCallBBRError = useCallback(
        (errorMessage: string) => {
            logger.error(`Failed to call BBR with kvhxUrl, error: ${errorMessage}`);
        },
        [logger]
    );

    return { logGetCustomerMarkingError, logGetStakeholderError, logCallBBRError };
};

const useRMOModal = (logger: TLogger) => {
    const logRMOError = useCallback(
        (errorMessage: string) => {
            logger.error(`An error occured while handling RMO: ${errorMessage}`);
        },
        [logger]
    );

    return { logRMOError };
};

/* ================================================ Main Component ================================================ */
const LoggerContext = createContext<ILoggerContext>({} as ILoggerContext);

export const LoggerContextProvider = ({
    values: [logEndpoint, appName, logLevel],
    children
}: ILoggerContextProviderWrapper) => {
    const logger = useMemo(() => LogFactory(logEndpoint, appName, logLevel), [logEndpoint, appName, logLevel]);

    useEffect(() => {
        logger.debug(`Starting up application ${logger.appName}`);
    }, [logger]);

    const { logCallDMRGetVehicleError, logCallMMGetVehicleDetailsError } = useVehiclePage(logger);

    const { logUpdateMileageError } = useMileageHelperModal(logger);
    const { logGetCarVerificationError } = useExperiencePage(logger);
    const {
        logUpdatePricingBasedOnDisabilitySumError,
        logUpdatePricingBasedOnMileageError,
        logUpdatePricingBasedOnDeductibleError,
        logHandleCoverageGroupSelectionError,
        logUpdatePackageError
    } = usePackagePage(logger);
    const { logHandlePackagesRequestError } = usePackageLoader(logger);
    const { logValidateBankAccountError } = usePaymentPage(logger);
    const { logFetchDMRDataError, logHandleContactProceedError } = useConfirmPage(logger);
    const { logFinalizePurchaseError, logFinalizePurchaseEmailError } = useFinalizePurchasePage(logger);

    const { logError, logWarn, logDebug } = useLogger(logger);
    const { logFetchContentError } = useFetchContent(logger);
    const { logGetCustomerMarkingError, logGetStakeholderError, logCallBBRError } = useUser(logger);
    const { logRMOError } = useRMOModal(logger);

    return (
        <LoggerContext.Provider
            value={useMemo(
                () => ({
                    logCallDMRGetVehicleError,
                    logCallMMGetVehicleDetailsError,
                    logUpdateMileageError,
                    logUpdatePricingBasedOnDisabilitySumError,
                    logUpdatePricingBasedOnMileageError,
                    logUpdatePricingBasedOnDeductibleError,
                    logHandleCoverageGroupSelectionError,
                    logUpdatePackageError,
                    logHandlePackagesRequestError,
                    logValidateBankAccountError,
                    logFetchDMRDataError,
                    logHandleContactProceedError,
                    logFinalizePurchaseError,
                    logFinalizePurchaseEmailError,
                    logFetchContentError,
                    logGetCustomerMarkingError,
                    logGetStakeholderError,
                    logCallBBRError,
                    logRMOError,
                    logGetCarVerificationError,
                    logError,
                    logWarn,
                    logDebug
                }),
                [
                    logCallDMRGetVehicleError,
                    logCallMMGetVehicleDetailsError,
                    logUpdateMileageError,
                    logUpdatePricingBasedOnDisabilitySumError,
                    logUpdatePricingBasedOnMileageError,
                    logUpdatePricingBasedOnDeductibleError,
                    logHandleCoverageGroupSelectionError,
                    logUpdatePackageError,
                    logHandlePackagesRequestError,
                    logValidateBankAccountError,
                    logFetchDMRDataError,
                    logHandleContactProceedError,
                    logFinalizePurchaseError,
                    logFinalizePurchaseEmailError,
                    logFetchContentError,
                    logGetCustomerMarkingError,
                    logGetStakeholderError,
                    logCallBBRError,
                    logRMOError,
                    logGetCarVerificationError,
                    logError,
                    logWarn,
                    logDebug
                ]
            )}
        >
            {children}
        </LoggerContext.Provider>
    );
};

export const useLoggerContext = () => useContext(LoggerContext);
